import * as React from 'react';

import PropTypes from 'prop-types';

import { Hidden } from '@coursera/cds-core';

import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/admin-dashboard/types';
import GlobalHelpButton from 'bundles/common/components/GlobalHelpButton';
import enterpriseAdminUser from 'bundles/enterprise-admin';
import { PrivateAuthoringPartnerContext } from 'bundles/enterprise-admin-custom-courses/components/PrivateAuthoringPartnerProvider';
import { getPrivateAuthoringPartnerIdForCourseBuilder } from 'bundles/enterprise-admin-custom-courses/utils/privateAuthoringUtils';
import AdminNavigationDropdown from 'bundles/enterprise-admin-navigation/components/AdminNavigationDropdown';
import CourseraHeader from 'bundles/enterprise-admin-navigation/components/CourseraHeader';
import MenuItem from 'bundles/enterprise-admin-navigation/components/MenuItem';
import NavMenu from 'bundles/enterprise-admin-navigation/components/NavMenu';
import { getMenuLinks } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import withManagedOrganizations from 'bundles/enterprise-admin-navigation/utils/withManagedOrganizations';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import GDPRBanner from 'bundles/marketing-consent/components/GDPRBanner';
import { isSkillTrackingExperimentEnabled } from 'bundles/skills-common/private/SkillTrackingExperiment';

type PropsFromCaller = {
  thirdPartyOrganization: AdminDashboardOrg;
};

type PropsFromOrganizations = {
  thirdPartyOrganizations: AdminDashboardOrg[];
};

type Props = PropsFromCaller & PropsFromOrganizations;

export class AdminHeaderAndNav extends React.Component<Props> {
  static contextTypes = {
    router: PropTypes.object,
    enableSkillsPlanningForOrg: PropTypes.bool,
  };

  renderMenuLinks(canUseCourseBuilder: boolean) {
    const { thirdPartyOrganization } = this.props;
    const { router, enableSkillsPlanningForOrg } = this.context;

    const isSkillTrackingEnabled = isSkillTrackingExperimentEnabled(thirdPartyOrganization.slug);

    return getMenuLinks(
      router.params,
      enableSkillsPlanningForOrg,
      canUseCourseBuilder,
      isSkillTrackingEnabled,
      thirdPartyOrganization.id
    ).map((item) => {
      const { id, href, text, requiredPermissions, phaseOneOverride } = item;
      return requiredPermissions ? (
        <EnterprisePermissionsRequired
          domain={requiredPermissions.domain}
          domainId={requiredPermissions.domainId}
          subdomain={requiredPermissions.subdomain}
          verb={requiredPermissions.verb}
          phaseOneOverride={phaseOneOverride}
          key={id}
        >
          <MenuItem
            isActive={router.isActive(href)}
            text={text}
            tag="link"
            thirdPartyOrganizationId={thirdPartyOrganization.id}
            htmlAttributes={{
              href,
              'data-e2e': `Menu_Link~${text}`,
            }}
          />
        </EnterprisePermissionsRequired>
      ) : (
        <MenuItem
          key={id}
          isActive={router.isActive(href)}
          text={text}
          tag="link"
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          htmlAttributes={{
            href,
            'data-e2e': `Menu_Link~${text}`,
          }}
        />
      );
    });
  }

  renderLeftChildren = (canUseCourseBuilder: boolean) => {
    return <NavMenu>{this.renderMenuLinks(canUseCourseBuilder)}</NavMenu>;
  };

  render() {
    const { thirdPartyOrganization, thirdPartyOrganizations } = this.props;

    const rightChildren = (
      <AdminNavigationDropdown
        isDirectSale={enterpriseAdminUser.isStandardContractUser()}
        isSelfServe={!enterpriseAdminUser.isStandardContractUser()}
        thirdPartyOrganization={thirdPartyOrganization}
        thirdPartyOrganizations={thirdPartyOrganizations}
      />
    );

    return (
      <div data-testid="AdminHeaderAndNav">
        <div data-testid="mobile">
          <Hidden mdUp>
            <CourseraHeader navHeight={66} leftChildren={null} rightChildren={rightChildren} />
          </Hidden>
        </div>
        <div data-testid="desktop">
          <Hidden smDown>
            <PrivateAuthoringPartnerContext.Consumer>
              {({ enterpriseAuthoringPartners }) => (
                <CourseraHeader
                  navHeight={66}
                  leftChildren={this.renderLeftChildren(
                    !!getPrivateAuthoringPartnerIdForCourseBuilder(enterpriseAuthoringPartners)
                  )}
                  rightChildren={rightChildren}
                />
              )}
            </PrivateAuthoringPartnerContext.Consumer>
          </Hidden>
        </div>
        <GlobalHelpButton />
        <GDPRBanner organizationType={thirdPartyOrganization.organizationType ?? undefined} />
      </div>
    );
  }
}

export default withManagedOrganizations<Props, PropsFromCaller>(AdminHeaderAndNav);
